import React, { useState, useEffect } from "react";
import { Button, IconButton, TextField } from "@mui/material";
import ThumbUpAltOutlinedIcon from "@mui/icons-material/ThumbUpAltOutlined";
import ThumbDownOffAltRoundedIcon from "@mui/icons-material/ThumbDownOffAltRounded";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import PsychologyAltOutlinedIcon from "@mui/icons-material/PsychologyAltOutlined";
import clsx from "clsx";
import styles from "./response-feedback.module.css";
import FeedbackApi from "../../api/FeedbackApi";
import { useAuth } from "../../context/AuthContext";
import LoadingSpinner from "../LoadingSpinner";

interface FeedbackFormData {
  feedback: string;
}

type ResponseFeedbackProps = {
  handleCopy?: () => void;
  handleThoughtsDrawerOpen?: () => void;
  messageId: string;
  sessionId: string;
  isPersisted?: boolean;
  feedback?: boolean | null;
};

const ResponseFeedback = ({
  handleCopy,
  handleThoughtsDrawerOpen,
  messageId,
  sessionId,
  feedback,
  isPersisted = false, // Default to false if not provided
}: ResponseFeedbackProps) => {
  const [liked, setLiked] = useState<boolean | null>(feedback ?? null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackData, setFeedbackData] = useState<FeedbackFormData>({
    feedback: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const { getAccessToken } = useAuth();

  // Sync state with feedback prop when it changes (e.g., when loading from session)
  useEffect(() => {
    setLiked(feedback ?? null);
  }, [feedback]);

  const handleLike = async () => {
    if (submitting || liked !== null || !isPersisted) return; // Block if not persisted
    setLiked(true);
    setShowFeedback(false);
    setSubmitting(true);

    try {
      const token = await getAccessToken();
      // Add timeout to prevent infinite pending
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error("Request timeout")), 10000)
      );
      await Promise.race([
        FeedbackApi.create(
          {
            feedback: "",
            rating: true,
            session_id: sessionId,
            message_id: messageId,
          },
          { accessToken: token || undefined }
        ),
        timeoutPromise,
      ]);
    } catch (e) {
      // Don't revert liked state on error - feedback was given
    } finally {
      setSubmitting(false);
    }
  };

  const handleDislike = () => {
    if (!isPersisted) return; // Block if not persisted
    setLiked(false);
    setShowFeedback(true);
  };

  const handleFeedbackChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setFeedbackData({ feedback: e.target.value });
  };

  const handleCancel = () => {
    // Reset all feedback state when user cancels
    setShowFeedback(false);
    setLiked(null);
    setFeedbackData({ feedback: "" });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitting || !isPersisted) return; // Block if not persisted
    setSubmitting(true);

    try {
      const token = await getAccessToken();
      // Add timeout to prevent infinite pending
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error("Request timeout")), 10000)
      );
      await Promise.race([
        FeedbackApi.create(
          {
            feedback: feedbackData.feedback.trim(),
            rating: false,
            session_id: sessionId,
            message_id: messageId,
          },
          { accessToken: token || undefined }
        ),
        timeoutPromise,
      ]);
      setFeedbackData({ feedback: "" });
      setShowFeedback(false);
    } catch (err) {
      // Still close the form on error/timeout
      setFeedbackData({ feedback: "" });
      setShowFeedback(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.responseFeedback}>
      <IconButton
        aria-label="Copy"
        title="Copy"
        className={styles.iconButton}
        onClick={handleCopy}
      >
        <ContentCopyOutlinedIcon fontSize="small" />
      </IconButton>
      <IconButton
        onClick={handleLike}
        aria-label="Like"
        title={!isPersisted ? "Please wait..." : "Like"}
        disabled={submitting || liked !== null || !isPersisted}
        className={clsx(styles.iconButton, liked === true && styles.active)}
      >
        {submitting && liked === true ? (
          <LoadingSpinner inline size="xs" />
        ) : (
          <ThumbUpAltOutlinedIcon fontSize="small" />
        )}
      </IconButton>
      <IconButton
        onClick={handleDislike}
        aria-label="Dislike"
        title={!isPersisted ? "Please wait..." : "Dislike"}
        disabled={submitting || liked !== null || !isPersisted}
        className={clsx(styles.iconButton, liked === false && styles.active)}
      >
        {submitting && liked === false ? (
          <LoadingSpinner inline size="xs" />
        ) : (
          <ThumbDownOffAltRoundedIcon fontSize="small" />
        )}
      </IconButton>
      {/* Disable chainof thoughts */}
      <IconButton
        className={styles.iconButton}
        onClick={handleThoughtsDrawerOpen}
      >
        <PsychologyAltOutlinedIcon fontSize="medium" />
      </IconButton>
      {showFeedback && (
        <form onSubmit={handleSubmit}>
          <div className={styles.feedbackForm}>
            <TextField
              fullWidth
              placeholder="Please enter your feedback..."
              value={feedbackData.feedback}
              multiline
              onChange={handleFeedbackChange}
              size="small"
              variant="outlined"
            />
            <div className={styles.buttonContainer}>
              <Button variant="outlined" onClick={handleCancel} size="small">
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                size="small"
                disabled={submitting}
              >
                Submit
              </Button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
};

export default ResponseFeedback;
